import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveMenuItem, setMenu, setNextActiveMenuItem, setPrevActiveMenuItem } from "../../redux/actions/menu/menu";
import { EXTERNAL_IN_PROGRESS } from "../../redux/actionTypes/actionsTypes";
import { initialMenuItem } from "../../redux/reducers/getMenu/getMenuReducer";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import CIXNavList from "../../assets/data/navigationList/CIXNavList";
import GLFNavList from "../../assets/data/navigationList/GLFNavList";
import HTLNavList from "../../assets/data/navigationList/HTLNavList";
import MTLNavList from "../../assets/data/navigationList/MTLNavList";
import LLENavList from "../../assets/data/navigationList/LLENavList";
import LTCNavList from "../../assets/data/navigationList/LTCNavList";
import RHONavList from "../../assets/data/navigationList/RHONavList";
import GEVNavList from "../../assets/data/navigationList/GEVNavList";
import CIXNavListInternal from "../../assets/data/navigationListInternal/CIXNavListInternal";
import GLFNavListInternal from "../../assets/data/navigationListInternal/GLFNavListInternal";
import HTLNavListInternal from "../../assets/data/navigationListInternal/HTLNavListInternal";
import MTLNavListInternal from "../../assets/data/navigationListInternal/MTLNavListInternal";
import LLENavListInternal from "../../assets/data/navigationListInternal/LLENavListInternal";
import LTCNavListInternal from "../../assets/data/navigationListInternal/LTCNavListInternal";
import RHONavListInternal from "../../assets/data/navigationListInternal/RHONavListInternal";
import Sidebar from "../../layouts/Sidebar/Sidebar";
import MenuItem from "../../types/menu/MenuItem";
import './PortalMenu.css';
import { useReadOnly } from "../../utils/ReadOnlyContext";
import { resetSaveData, resetSaveResult, resetSaveResultError, setSaving } from "../../redux/actions/saveData/saveData";
import { SaveResultState } from "../../redux/reducers/getSaveData/saveResultReducer";
import { toast } from "react-toastify";
import { useNavigation } from "../../utils/context/NavigationContext";
import { Trans, useTranslation } from "react-i18next";
import ConfirmationDialog from "../common/Modal/ConfirmationDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import ToolTip from "../common/ToolTip/ToolTip";
import AGSNavList from "../../assets/data/navigationList/AGSNavList";
import { INTERNAL_IN_PROGRESS } from "../../redux/actionTypes/actionsTypes";
import { selectGetAggregateSiteAssessmentInfo, selectMenu, selectProfileGetDetails, selectProfileGetOverview, selectSaveResult } from "../../redux/selectors/exportSelectorPaths";
import { isValidationResult, useFormErrorContext } from "../../utils/context/FormErrorContext";
import { useFormWarningContext } from "../../utils/context/FormWarningContext";
import reportMenuDuration from "../../utils/GA4EventHelpers/reportMenuDuration";
import { getProfileDetails, resetGetProfileDetails } from "../../redux/actions/profile/profileDetails";
import { ProfileDetailsState } from "../../redux/reducers/getProfile/getProfileDetailsReducer";
import { keysOfFiscalDetailsType } from "../../types/profileDetails/FiscalDetailsType";
import { pickFromProfileDetailsType } from "../../types/profileDetails/ProfileDetailsType";
import { AggregateSiteAssessmentInfoState } from "../../types/assessment_info/AggregateSiteAssessmentInfoState";
import { AggregateSiteAssessmentInfo } from "../../types/assessment_info/aggregateSites/AggregateSiteData";
import { getAssessmentInfo, resetGetAssessmentInfo } from "../../redux/actions/assessmentInfo/assessmentInfo";

function PortalMenu(): JSX.Element {
    const menuState = useSelector(selectMenu);
    const { direction, setDirection } = useNavigation();
    const getProfileDetailsState: ProfileDetailsState = useSelector(selectProfileGetDetails)
    const assessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const [collapsed, setCollapsed] = useState(false);
    const dispatch = useDispatch();
    const { isReadOnly } = useReadOnly()
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false
    const campaignStatusCode = String(sessionStorage.getItem("campaignStatusCode"))
    const currentSnapshotId = String(sessionStorage.getItem("currentSnapshotId"))
    const profileTypeCode = profileOverview.profile?.profile_type_code
    const { t } = useTranslation(["common"]);
    const { clearErrors } = useFormErrorContext();
    const { warningDisplayed, setWarningDisplayed } = useFormWarningContext();
    const hasError = useRef<boolean>(false)
    const hasWarning = useRef<boolean>(false)

    // PIER2-1881: The reason for the commercial record duplication is because the resetSaveResult in the setDynamicMenu function runs before
    // all backend responses return, resetting loading count to 0, and subsequent responses cause the loading count to go into the negatives,
    // not triggering the condition to refresh the data on the commercial page. This state is introduced to so the resetSaveResult in the
    // setDynamicMenu function runs only the fist time setDynamicMenu function is called.
    const [isDynamicMenuSet, setIsDynamicMenuSet] = useState(false)

    // Used compare Menu ID, the output is "a is $result to/than b"
    const compareMenuId = (a: number, b: number): "equal" | "larger" | "smaller" => {
        if (a === b) {
            return "equal";
        }
        else {
            const x = a.toString();
            const y = b.toString();
            const maxLength = Math.max(x.length, y.length);
            const maxedLengthA = x + ("0").repeat(maxLength - x.length);
            const maxedLengthB = y + ("0").repeat(maxLength - y.length);
            if (parseInt(maxedLengthA) > parseInt(maxedLengthB)) {
                return "larger";
            } else if (parseInt(maxedLengthA) < parseInt(maxedLengthB)) {
                return "smaller";
            } else {
                return "equal";
            }
        }
    }

    useEffect(() => {
        if (currentSnapshotId) {
            dispatch(resetGetProfileDetails())
            dispatch(getProfileDetails(currentSnapshotId))
            dispatch(resetGetAssessmentInfo())
            if (profileTypeCode === "AGS") {
                dispatch(getAssessmentInfo(currentSnapshotId, profileTypeCode))
            }
        }
    }, [currentSnapshotId, dispatch, profileTypeCode])

    useEffect(() => {

        const isWIP = () => {
            let isWIP = false
            const campaignId = sessionStorage.getItem("campaignId")
            if (campaignId) {
                const campaigns = [profileOverview.profile.current_campaign].concat(profileOverview.profile.previous_campaigns)
                const campaign = campaigns.find((c) => c && c["campaignId"] === Number(campaignId))
                if (campaign && campaign.statusCode === EXTERNAL_IN_PROGRESS) {
                    isWIP = true
                }
            }
            return isWIP
        }

        const setDynamicMenu = (profileType: string): void => {
            let menuList: MenuItem[] = []
            switch (profileType) {
                case 'BIP':
                    menuList = structuredClone(isInternalUser ? CIXNavListInternal : CIXNavList)
                    break;
                case 'GLF':
                    menuList = structuredClone(isInternalUser ? GLFNavListInternal : GLFNavList)
                    break;
                case 'HTL':
                    menuList = structuredClone(isInternalUser ? HTLNavListInternal : HTLNavList)
                    break;
                case 'LRH':
                    menuList = structuredClone(isInternalUser ? HTLNavListInternal : HTLNavList)
                    break;
                case 'MTL':
                    menuList = structuredClone(isInternalUser ? MTLNavListInternal : MTLNavList)
                    break;
                case 'LLE':
                    menuList = structuredClone(isInternalUser ? LLENavListInternal : LLENavList)
                    break;
                case 'LTC':
                    menuList = structuredClone(isInternalUser ? LTCNavListInternal : LTCNavList)
                    break;
                case 'RHO':
                    menuList = structuredClone(isInternalUser ? RHONavListInternal : RHONavList)
                    break;
                case 'GEV':
                    menuList = structuredClone(GEVNavList)
                    break;
                case 'AGS':
                    menuList = structuredClone(AGSNavList)
                    break;
                default:
                    menuList = []
            }
            // Only show analysis menu item for internal users and INTERNAL_IN_PROGRESS status
            if (!isInternalUser || campaignStatusCode !== INTERNAL_IN_PROGRESS) {
                const idx = menuList.findIndex((menu) => menu.name === "incomeExpense")
                if (idx >= 0) {
                    const analysisIdx = menuList[idx]["subItems"]?.findIndex((menu => menu.name === "analysis"))
                    analysisIdx && analysisIdx !== -1 && menuList[idx]["subItems"]?.splice(analysisIdx, 1)
                }
            }
            // Only show submit menu item for WIP status
            if (!isWIP()) {
                menuList = menuList.filter((menu) => menu.name !== "submit")
            }
            if (isReadOnly) {
                menuList = menuList.filter(item => item.name !== "submit")
            }

            if (profileTypeCode === "AGS" && !assessmentInfoState.loading) {
                const data = assessmentInfoState.formData as AggregateSiteAssessmentInfo
                if (data?.licenseOperationTypeCode === "N") {
                    menuList = menuList.filter((menu) => !["licensedLands", "licensedPonds", "unlicensedLands", "documents", "agsSummary"].includes(menu.name))
                }
            }

            const fiscalDetails = pickFromProfileDetailsType(getProfileDetailsState.data, keysOfFiscalDetailsType)
            if (fiscalDetails.ownerOccupiedFlag) {
                menuList = menuList.filter((menu) => !["commercial", "apartment"].includes(menu.name))
            }

            // For Multi-Res and Mixed Use, add Utilities and ResidentialParking menu items
            if (["340", "341", "352", "361", "602", "470", "472", "478"].includes(propertyCode)) {
                const apartmentItem = menuList.find((menu) => menu.name === "apartment")
                const commercialItem = menuList.find((menu) => menu.name === "commercial")
                // Show Apartment before Commercial in navigation
                if (propertyCode !== "602" && commercialItem && apartmentItem && apartmentItem.id > commercialItem.id) {
                    const tempList = [...menuList]
                    const tempComItem = { ...commercialItem }
                    commercialItem.id = apartmentItem.id
                    if (commercialItem.subItems) {
                        for (let i = 0; i < commercialItem.subItems.length; i++) {
                            commercialItem.subItems[i].id = parseInt(apartmentItem.id + commercialItem.subItems[i].id.toString().substring(1))
                        }
                    }
                    tempList[apartmentItem.id - 1] = commercialItem
                    apartmentItem.id = tempComItem.id
                    if (apartmentItem.subItems) {
                        for (let i = 0; i < apartmentItem.subItems.length; i++) {
                            apartmentItem.subItems[i].id = parseInt(tempComItem.id + apartmentItem.subItems[i].id.toString().substring(1))
                        }
                    }
                    tempList[tempComItem.id - 1] = apartmentItem
                    menuList = tempList
                }
                if (apartmentItem) {
                    if (apartmentItem.subItems) {
                        const utilitiesItem = apartmentItem.subItems.find((menu) => menu.name === "utilities")
                        const resParkingItem = apartmentItem.subItems.find((menu) => menu.name === "residentialParking")
                        const otherItems = apartmentItem.subItems.filter((menu) => menu.name !== "utilities" && menu.name !== "residentialParking")
                        let baseId = apartmentItem.id * 10
                        if (otherItems) {
                            const len = otherItems.length
                            const currentLastSubitem = apartmentItem.subItems[len - 1]
                            baseId = currentLastSubitem.id
                        }
                        const toBeAdded = [] as MenuItem[]
                        if (!utilitiesItem) {
                            const newUtilitiesItem = {
                                id: baseId + 1,
                                name: 'utilities',
                                componentName: 'Utilities',
                                complete: false
                            }
                            toBeAdded.push(newUtilitiesItem)
                        }
                        if (!resParkingItem) {
                            const newResParkingItem = {
                                id: baseId + 2,
                                name: 'residentialParking',
                                componentName: 'ResidentialParking',
                                complete: false,
                            }
                            toBeAdded.push(newResParkingItem)
                        }
                        apartmentItem.subItems = otherItems.concat(toBeAdded)
                    }
                }
                // Show Commercial before Apartment in navigation
            } else if (["402", "403", "409", "417", "419", "425", "426", "427", "428", "429", "430", "431", "433", "434", "435", "436", "438", "481", "483", "580"].includes(propertyCode)) {
                const apartmentItem = menuList.find((menu) => menu.name === "apartment")
                const commercialItem = menuList.find((menu) => menu.name === "commercial")
                if (commercialItem && apartmentItem && commercialItem.id > apartmentItem.id) {
                    const tempList = [...menuList]
                    const tempAptItem = { ...apartmentItem }
                    apartmentItem.id = commercialItem.id
                    if (apartmentItem.subItems) {
                        for (let i = 0; i < apartmentItem.subItems.length; i++) {
                            apartmentItem.subItems[i].id = parseInt(commercialItem.id + apartmentItem.subItems[i].id.toString().substring(1))
                        }
                    }
                    tempList[commercialItem.id - 1] = apartmentItem
                    commercialItem.id = tempAptItem.id
                    if (commercialItem.subItems) {
                        for (let i = 0; i < commercialItem.subItems.length; i++) {
                            commercialItem.subItems[i].id = parseInt(tempAptItem.id + commercialItem.subItems[i].id.toString().substring(1))
                        }
                    }
                    tempList[tempAptItem.id - 1] = commercialItem
                    menuList = tempList
                }
            }
            const isModifiedSubmission = isInternalUser && sessionStorage.getItem("editMode");
            if (isModifiedSubmission) {
                const tempList = [...menuList]
                const commercialItem = tempList.findIndex((menu) => menu.name === "commercial");
                if (commercialItem >= 0) {
                    const commercialItemSubItems = tempList[commercialItem].subItems
                    if (commercialItemSubItems) {
                        const leaseInfoItem = commercialItemSubItems.findIndex((menu) => menu.name === "leaseInformationTable");
                        if (leaseInfoItem >= 0) {
                            commercialItemSubItems[leaseInfoItem].componentName = "LeaseInfoInternal"
                            menuList = tempList;
                        }
                    }
                }
            }
            if (!isDynamicMenuSet) {
                dispatch(resetSaveData());
                dispatch(resetSaveResult());
                setIsDynamicMenuSet(true);
            }
            if (profileTypeCode === "AGS") {
                if (!assessmentInfoState.loading && Object.keys(assessmentInfoState.formData)?.length)
                    dispatch(setMenu(menuList))
            } else {
                dispatch(setMenu(menuList))
            }
            if (menuState.activeItem.id === initialMenuItem.id) {
                if (menuList.length > 0) {
                    dispatch(setActiveMenuItem(menuList[0])) // do not trigger reportMenuDuration here to avoid event triggers with null values
                }
            }
        }

        if (!assessmentInfoState.loading && assessmentInfoState.status && assessmentInfoState.status === 200) {
            setDynamicMenu(profileOverview.profile.profile_type_code)
        }

        if (!profileOverview.loading && profileOverview.status && profileOverview.status === 200 &&
            !getProfileDetailsState.loading && getProfileDetailsState.status && getProfileDetailsState.status === 200) {
            setDynamicMenu(profileOverview.profile.profile_type_code)
        }
    }, [campaignStatusCode, dispatch, getProfileDetailsState.data, getProfileDetailsState.loading, getProfileDetailsState.status, isInternalUser, isReadOnly, menuState.activeItem.id, profileOverview.loading, profileOverview.profile.current_campaign, profileOverview.profile.previous_campaigns, profileOverview.profile.profile_type_code, profileOverview.status, propertyCode, assessmentInfoState, profileTypeCode, isDynamicMenuSet]);

    useEffect(() => {
        setWarningDisplayed(false);
    }, [menuState.activeItem.id, setWarningDisplayed])

    const navigateToNextActiveMenu = () => {
        if (menuState.nextActiveItem.id) {
            dispatch(setActiveMenuItem(menuState.nextActiveItem));
            reportMenuDuration(menuState);
        }
    }

    const navigateToPrevActiveMenu = () => {
        if (menuState.prevActiveItem.id) {
            dispatch(setActiveMenuItem(menuState.prevActiveItem));
            reportMenuDuration(menuState);
        }
    }

    useEffect(() => {
        // When the nextActiveItem is different from the activeItem, the user is attempting to navigate to another page,
        // if there is no changesPending or is in isReadOnly mode, proceed to another page, otherwise trigger setSaving.
        // Once the data is successfully saved, the page component should resetSaveData, making changesPending false
        if (direction === "forward" && menuState.nextActiveItem.id !== 0 && (compareMenuId(menuState.activeItem.id, menuState.nextActiveItem.id) === "smaller"
            || ((menuState.activeItem.selectedTabIndex ?? -1) !== (menuState.nextActiveItem.selectedTabIndex ?? -1)))) {
            if (isReadOnly) {
                clearErrors()
                navigateToNextActiveMenu();
            } else if (menuState.activeItem.id) {
                // console.log("direction forward before dispatch setSaving, changesPending is", saveDataState.changesPending)
                confirmProceed()
            } else {
                navigateToNextActiveMenu();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isReadOnly, menuState.activeItem.id, menuState.activeItem.selectedTabIndex, menuState.nextActiveItem.id, menuState.nextActiveItem.selectedTabIndex, direction]);

    useEffect(() => {
        // When the prevActiveItem is different from the activeItem, the user is attempting to navigate backwards,
        // if there is no changesPending or is in isReadOnly mode, proceed to another page, otherwise trigger setSaving.
        // Once the data is successfully saved, the page component should resetSaveData, making changesPending false
        if (direction === "backward" && menuState.prevActiveItem.id !== 0 && (compareMenuId(menuState.activeItem.id, menuState.prevActiveItem.id) === "larger"
            || ((menuState.activeItem.selectedTabIndex ?? -1) !== (menuState.nextActiveItem.selectedTabIndex ?? -1)))) {
            if (isReadOnly) {
                clearErrors()
                navigateToPrevActiveMenu();
            } else if (menuState.activeItem.id) {
                // console.log("direction backward before dispatch setSaving, changesPending is ", saveDataState.changesPending)
                confirmProceed()
            } else {
                navigateToPrevActiveMenu();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isReadOnly, menuState.activeItem.id, menuState.activeItem.selectedTabIndex, menuState.prevActiveItem.id, menuState.prevActiveItem.selectedTabIndex, direction]);

    // If saveResult status is 200, reset everything, otherwise show the error message, set saving to false, and clear setNextActiveMenuItem
    useEffect(() => {
        if (saveResultState.status) {
            if (saveResultState.result && isValidationResult(saveResultState.result)){
                if (saveResultState.result.errors.length > 0 && !hasError.current) {
                    hasError.current = true
                }
                if (saveResultState.result.warnings.length > 0 && !hasWarning.current) {
                    hasWarning.current = true
                }
                dispatch(resetSaveResultError());
            }
            if (saveResultState.loadingCount < 1) {
                if (!hasError.current && !hasWarning.current) {
                    sessionStorage.removeItem("hasWarnings")
                    direction === "forward" ? navigateToNextActiveMenu() : navigateToPrevActiveMenu();
                } else {
                    if (!hasError.current) {
                        if (hasWarning.current) {
                            if (!sessionStorage.getItem("hasWarnings")) {
                                sessionStorage.setItem("hasWarnings", "true")
                            }
                            if (warningDisplayed) {
                                direction === "forward" ? navigateToNextActiveMenu() : navigateToPrevActiveMenu();
                            }
                        }
                    }
                    if (direction === "forward") {
                        dispatch(setNextActiveMenuItem(initialMenuItem));
                        setDirection("forward")
                    }
                    else {
                        dispatch(setPrevActiveMenuItem(initialMenuItem));
                        setDirection("backward")
                    }

                    if (saveResultState.message) {
                        toast.error(saveResultState.message);
                    }
                }
                hasWarning.current = false;
                hasError.current = false;
                dispatch(resetSaveResult());
            }
            dispatch(resetSaveData());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResultState, dispatch, direction]);

    const handleCollapsedChange = () => {
        setCollapsed(!collapsed);
    }

    useEffect(() => {
        collapsed ?
            document.documentElement.style.setProperty('--sidebar-width', '1rem') :
            document.documentElement.style.setProperty('--sidebar-width', '15rem');
    }, [collapsed]);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(() => (() => { console.log() }))
    const [cancelAction, setCancelAction] = useState(() => (() => { console.log() }))
    const [confirmBody, setConfirmBody] = useState(<></>)

    const confirmProceed = () => {
        const confirmNavigation = (activeMenuItem: MenuItem) => {
            if (isInternalUser) {
                return false
            }
            return activeMenuItem.confirmNavigation ? true : false
        }

        const allInputHasNoValue = () => {
            const allInputFields = Array.from(document.getElementsByTagName("input"))
            const allSelectFields = Array.from(document.getElementsByTagName("select"))
            const allTextareaFields = Array.from(document.getElementsByTagName("textarea"))
            const allFields = [...allInputFields, ...allSelectFields, ...allTextareaFields]
            if (allFields.length === 0) {
                return false
            }
            let allInputHasNoValue = true
            allFields.forEach((inputField) => {
                if (allInputHasNoValue) {
                    if (inputField instanceof HTMLSelectElement) {
                        if (inputField.selectedIndex) {
                            allInputHasNoValue = false
                        }
                    } else if (inputField instanceof HTMLTextAreaElement) {
                        if (inputField.value) {
                            allInputHasNoValue = false
                        }
                    } else {
                        const inputFieldType = inputField.type
                        if (["radio", "checkbox"].includes(inputFieldType)) {
                            if (inputField.checked) {
                                allInputHasNoValue = false
                            }
                        } else if (inputField.value) {
                            allInputHasNoValue = false
                        }
                    }
                }
            })
            return allInputHasNoValue
        }

        const onConfirmAction = () => {
            setShowConfirmModal(false)
            dispatch(setSaving())
        }

        if (confirmNavigation(menuState.activeItem) && allInputHasNoValue()) {
            const onCancelAction = () => {
                setShowConfirmModal(false)
                dispatch(setNextActiveMenuItem(initialMenuItem))
                dispatch(setPrevActiveMenuItem(initialMenuItem))
            }
            setCancelAction(() => onCancelAction)
            setConfirmAction(() => onConfirmAction)
            setConfirmBody(<>
                <Trans t={t}
                    i18nKey="confirm-nochange-before-proceed"
                    ns="common"
                    components={{ p: <p /> }}
                />
            </>)
            setShowConfirmModal(true)
        } else {
            onConfirmAction()
        }
    }

    return (<>
        {isInternalUser && (
            <ToolTip placement="top" className="sidebar-collapse-btn" contents={
                <button
                    onClick={handleCollapsedChange}
                    aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
                </button>
            }>
                Click to toggle sidebar
            </ToolTip>

        )}
        <Sidebar
            collapsed={collapsed}
            year={String(process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR)}
            items={menuState.menu}
            compareId={compareMenuId}
            profileTypeCode={profileTypeCode}
        />
        {showConfirmModal &&
            <ConfirmationDialog
                onCancel={() => cancelAction()}
                onConfirm={() => confirmAction()}
                cancelText={t("confirm-nochange-no", { ns: "common" })}
                headerClass="no-border" buttonGroupClass="justify-content-center no-border"
            >
                {confirmBody}
            </ConfirmationDialog>
        }
    </>)
}

export default PortalMenu;
